import {
  AdminSelect,
  AdminSingleLocationPicker,
  Div,
  Icon,
  lightTheme,
  SimpleLocationEntity,
  SupportTicketType,
  X,
} from '@vgrubs/components';
import { Dispatch, SetStateAction } from 'react';
import { TICKET_TYPE_OPTIONS } from '../helpers/supportTicketLabels';

/**
 * "No location filter", as something the picker can hold.
 *
 * The shared picker selects the first location it loads whenever nothing is
 * selected - right for the settings screen it was built for, wrong for a
 * filter, where it would silently hide every other location's tickets the
 * moment the page opened. A non-null value is what stops that, and it doubles
 * as the label.
 */
export const ALL_LOCATIONS = {
  id: 0,
  name: 'All locations',
} as SimpleLocationEntity;

type Props = {
  type: SupportTicketType | null;
  onTypeChange: (value: string) => void;
  location: SimpleLocationEntity;
  setLocation: Dispatch<SetStateAction<SimpleLocationEntity>>;
  /** Fires when a location is actually picked, which is what hits the URL. */
  onLocationPicked: (location: SimpleLocationEntity | null) => void;
};

/**
 * What kind of request, and whose. Laid out like every other admin list's
 * filter row: equal halves that stack on a phone, search living in the header.
 */
export const SupportTicketFilters = ({
  type,
  onTypeChange,
  location,
  setLocation,
  onLocationPicked,
}: Props) => {
  const filtered = location.id !== ALL_LOCATIONS.id;

  return (
    <Div
      $flex
      $between
      $columnSM
      $gap="32px"
      $gapSM="8px"
      /* No top margin: TabsWrapper above already carries 32px of padding
         under the tabs, and stacking a margin on it opens a gap. */
      $m="0 0 24px 0"
      $mSM="0 0 20px 0"
    >
      <Div $width="50%" $widthSM="100%" $flex $alignCenter $gap="8px">
        <Div $width="100%">
          <AdminSingleLocationPicker
            selectedLocation={location}
            setSelectedLocation={
              setLocation as Dispatch<
                SetStateAction<SimpleLocationEntity | null>
              >
            }
            handleSubmit={onLocationPicked}
          />
        </Div>

        {/* The picker has no way back to everything - it was built for a screen
            where one location is always the answer. */}
        {filtered && (
          <Icon
            $pointer
            $size="16"
            title="Show every location"
            onClick={() => {
              setLocation(ALL_LOCATIONS);
              onLocationPicked(null);
            }}
          >
            <X fill={lightTheme.darkGray} />
          </Icon>
        )}
      </Div>

      <Div $width="50%" $widthSM="100%">
        <AdminSelect
          value={type ?? ''}
          onChange={(event) => onTypeChange(event.target.value)}
          $shadowDesign
          options={
            <>
              <option value="">All types</option>
              {TICKET_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </>
          }
        />
      </Div>
    </Div>
  );
};
